import React, { useState, useMemo } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import ListPageHeader from '@shared/ListPageHeader';
import TableIndex from '@shared/table/TableIndex';
import type { TableService, SupportRule } from '@shared/table/TableIndex';
import { workOrderService } from '@services/operator/workOrderService';
import { MultipleValue } from '@shared/table/Rules/component';
import { formatDateTime } from '@utils/dateTime';
import { useMerchant } from '@contexts/operator/MerchantContext';
import { useIsStandard } from '@contexts/operator/PlanContext';
import {
  WORK_ORDER_STATUSES,
  WORK_ORDER_STATUS_COLORS,
  useWorkOrderStatusLabel,
} from '@constants/workOrder';

interface WorkOrderRow {
  id: string;
  name?: string;
  barcode?: string;
  status?: string;
  merchant_name?: string;
  scheduled_at?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
}

const WorkOrdersList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lastModified, setLastModified] = useState(0);
  const { merchants } = useMerchant() as { merchants: Array<{ id: string; name: string }> };
  const isStandard = useIsStandard();
  const workOrderStatusLabel = useWorkOrderStatusLabel();

  const columns = useMemo<unknown[]>(() => [
    {
      nameKey: 'operator.work_orders.list.columns.status',
      width: '120px',
      cell: ({ status }: WorkOrderRow) => {
        const color = (status && WORK_ORDER_STATUS_COLORS[status]) || 'default';
        return <Tag color={color}>{workOrderStatusLabel(status)}</Tag>;
      }
    },
    {
      nameKey: 'models.work_order.name',
      sortable: true,
      sortFieldName: 'work_orders.name',
      cell: ({ id, barcode, name }: WorkOrderRow) => (
        <Link to={`/work_orders/${id}`}>
            <span style={{ fontWeight: 500 }}>{name}</span>
            <div style={{ color: '#999', fontSize: 12 }}>{barcode}</div>
        </Link>
      )
    },
    // 單一 merchant 模式下不顯示 merchant 欄位
    ...(isStandard ? [] : [{
      nameKey: 'operator.work_orders.list.columns.merchant',
      selector: ({ merchant_name }: WorkOrderRow) => merchant_name
    }]),
    {
        nameKey: 'operator.work_orders.list.columns.scheduled_at',
        sortable: true,
        sortFieldName: 'work_orders.scheduled_at',
        cell: ({ scheduled_at }: WorkOrderRow) => scheduled_at ? formatDateTime(scheduled_at) : '-'
    },
    {
        nameKey: 'operator.work_orders.list.columns.created_at',
        sortable: true,
        sortFieldName: 'work_orders.created_at',
        cell: ({ created_at }: WorkOrderRow) => created_at ? formatDateTime(created_at) : '-'
    }
  ], [t, isStandard, workOrderStatusLabel]);

  const supportRules = useMemo<SupportRule[]>(() => [
     {
        label: t('operator.work_orders.list.filter_tooltips.status.title'),
        component: MultipleValue,
        inputName: 'status',
        defaultOptions: WORK_ORDER_STATUSES.map(s => ({
            label: workOrderStatusLabel(s),
            value: s
        })),
        createable: false,
        tooltip: { content: t('operator.work_orders.list.filter_tooltips.status.content') }
     },
     // 單一 merchant 模式下不顯示 merchant 篩選器
     ...(isStandard ? [] : [{
        label: t('operator.work_orders.list.filter_tooltips.merchant.title'),
        component: MultipleValue,
        inputName: 'merchant_id',
        defaultOptions: merchants.map((m: { id: string; name: string }) => ({ label: m.name, value: m.id })),
        createable: false,
        tooltip: { content: t('operator.work_orders.list.filter_tooltips.merchant.content') }
     }]),
     {
        label: t('operator.work_orders.list.filter_tooltips.search.title'),
        component: MultipleValue,
        inputName: 'search',
        createable: true,
        tooltip: { content: t('operator.work_orders.list.filter_tooltips.search.content') }
     },
     {
        label: t('operator.work_orders.list.filter_tooltips.input_product_sku.title'),
        component: MultipleValue,
        inputName: 'input_product_sku',
        createable: true,
        tooltip: { content: t('operator.work_orders.list.filter_tooltips.input_product_sku.content') }
     },
     {
        label: t('operator.work_orders.list.filter_tooltips.output_product_sku.title'),
        component: MultipleValue,
        inputName: 'output_product_sku',
        createable: true,
        tooltip: { content: t('operator.work_orders.list.filter_tooltips.output_product_sku.content') }
     }
  ], [t, merchants, isStandard, workOrderStatusLabel]);

  return (
    <div>
      <ListPageHeader
        items={['production', 'production.work_orders']}
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/work_orders/new')}
          >
            {t('common.button.new')}
          </Button>
        }
      />
      <TableIndex
        columns={columns}
        searchable={{
          match: 'contains',
          searchFields: [
            t('models.work_order.barcode'),
            t('models.work_order.name'),
          ],
        }}
        tableId="WorkOrdersList"
        defaultQuery={{ sorting_field: 'work_orders.created_at', sorting_direction: 'desc' }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={workOrderService as TableService}
      />
    </div>
  );
};

export default WorkOrdersList;
